// External guest chat. Opened via the URL an employee's "Invite an external
// guest" action emailed:  /#guest-chat?token=<64-hex>
//
// Standalone (no sidebar / no normal login) — mirrors PublicIntake/PublicApply.
// Accept-once flow: POST /api/guest/invite/:token/accept exchanges the
// invite token for a guestToken (signed with a different server secret than
// normal logins — see server/middleware/guestAuth.js), which this page then
// uses for every /api/guest/* call and the socket connection. The guestToken
// is cached in localStorage keyed by the invite token so reopening the same
// link doesn't show the "Join" screen again.
import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { Send, Paperclip, Download, FileText, Loader2, Smile, Mic, Square, X, MoreHorizontal, Pencil, Trash2, Copy, Plus } from "lucide-react";
import { QUICK_REACTIONS, EMOJI_CATEGORIES } from "../chat/emoji";
import { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";

const GUEST_SESSION_PREFIX = 'hrms_guest_session_';

const URL_SPLIT_RE = /(https?:\/\/[^\s]+)/g;
const isUrl = (s) => /^https?:\/\//.test(s);
const renderBody = (body) => body ? body.split(URL_SPLIT_RE).map((part, i) => isUrl(part)
  ? <a key={i} href={part} target="_blank" rel="noreferrer" className="underline break-all" style={{ color: 'inherit' }}>{part}</a>
  : <React.Fragment key={i}>{part}</React.Fragment>) : body;

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / (1024 * 1024)).toFixed(1) + ' MB';
};
const fmtTime = (t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const attachmentKindFor = (file) => file.type.startsWith('image/') ? 'image' : file.type.startsWith('audio/') ? 'audio' : file.type.startsWith('video/') ? 'video' : 'file';
const EDIT_WINDOW_MS = 10 * 60 * 1000;
const jwtPayload = (t) => { try { return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); } catch { return {}; } };
const attachmentUrl = (m) => m.attachment_path ? uploadUrl(m.attachment_path) : m._previewUrl;

const GuestAttachment = ({ m, onOpenImage }) => {
  const url = attachmentUrl(m);
  if (!url) return null;
  if (m.attachment_kind === 'image') {
    return <img src={url} alt={m.attachment_name || 'image'} onClick={() => onOpenImage(url)} className="max-w-[220px] max-h-[220px] rounded-lg object-cover cursor-pointer block" />;
  }
  if (m.attachment_kind === 'audio') {
    return <div className="p-2 rounded-lg bg-slate-100 border border-slate-200 min-w-[220px]"><audio controls src={url} className="w-full h-[34px]" /></div>;
  }
  if (m.attachment_kind === 'video') {
    return <video controls src={url} className="max-w-[260px] max-h-[260px] rounded-lg bg-black block" />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" download={m.attachment_name} className="flex items-center gap-[10px] px-[10px] py-[9px] rounded-lg no-underline min-w-[200px] bg-slate-100 border border-slate-200 hover:bg-slate-200 transition-colors">
      <div className="w-[36px] h-[36px] rounded-lg bg-slate-500 flex items-center justify-center flex-shrink-0"><FileText size={18} color="#fff" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-slate-800 truncate">{m.attachment_name || 'File'}</div>
        <div className="text-[11px] text-slate-500">{fmtBytes(m.attachment_size)}</div>
      </div>
      <Download size={16} className="flex-shrink-0 text-slate-500" />
    </a>
  );
};

async function guestFetch(path, { method = 'GET', token, body, isForm } = {}) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body && !isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${BASE}${path}`, { method, headers, body: isForm ? body : (body ? JSON.stringify(body) : undefined) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

const EmojiGrid = ({ onPick }) => (
  <div className="max-h-[240px] w-[260px] overflow-y-auto p-[6px]">
    {EMOJI_CATEGORIES.map(cat => (
      <div key={cat.label} className="mb-[4px]">
        <div className="text-[9.5px] font-bold uppercase tracking-wide text-slate-400 px-[4px] pt-[4px] pb-[2px] sticky top-0 bg-white">{cat.label}</div>
        <div className="grid grid-cols-8 gap-[1px]">
          {cat.emojis.map(emoji => (
            <button key={emoji} type="button" onClick={() => onPick(emoji)} className="text-[17px] bg-transparent border-0 cursor-pointer p-[3px] rounded-md hover:bg-slate-100 leading-none">{emoji}</button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Small fixed-position popover anchored to a button's screen position (stays inside the viewport).
const Popover = ({ anchor, width, height, onClose, children }) => {
  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));
  const top = anchor.bottom + 6 + height > window.innerHeight ? Math.max(8, anchor.top - height - 6) : anchor.bottom + 6;
  return (
    <>
      <div className="fixed inset-0 z-[590]" onClick={onClose} />
      <div style={{ position: 'fixed', top, left, width }} className="z-[600] bg-white border border-slate-200 rounded-xl shadow-[0_10px_28px_rgba(15,23,42,0.2)] py-1">{children}</div>
    </>
  );
};

export default function GuestChat() {
  const rawQuery = window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?'))
    : window.location.search;
  const token = new URLSearchParams(rawQuery).get('token') || '';
  const sessionKey = GUEST_SESSION_PREFIX + token;

  const [state, setState] = useState('loading'); // loading | join | chatting | error
  const [errorMsg, setErrorMsg] = useState('');
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState('');
  const [joining, setJoining] = useState(false);
  const [session, setSession] = useState(null); // { guestToken, groupId, groupName }
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [menu, setMenu] = useState(null);        // { id, anchor, picker }
  const [who, setWho] = useState(null);          // { id, emoji, anchor, rows }
  const [composerEmoji, setComposerEmoji] = useState(null); // anchor
  const [editing, setEditing] = useState(null);  // { id, text, err }
  const [lightbox, setLightbox] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
  const recorderRef = useRef(null), recChunksRef = useRef([]), recCancelRef = useRef(false), recTimerRef = useRef(null);

  const myId = session ? jwtPayload(session.guestToken).id : null;
  const tok = session?.guestToken;
  const gp = session ? `/api/guest/groups/${session.groupId}` : '';

  useEffect(() => {
    if (!token) { setState('error'); setErrorMsg('Missing invite link token. Please re-open the link you were sent.'); return; }
    try {
      const cached = JSON.parse(localStorage.getItem(sessionKey) || 'null');
      if (cached?.guestToken) { setSession(cached); setState('chatting'); return; }
    } catch { /* ignore */ }
    guestFetch(`/api/guest/invite/${token}`)
      .then(d => { setInvite(d); setName(d.name || ''); setState('join'); })
      .catch(e => { setErrorMsg(e.message); setState('error'); });
  }, [token, sessionKey]);

  const join = () => {
    setJoining(true);
    guestFetch(`/api/guest/invite/${token}/accept`, { method: 'POST', body: { name: name.trim() } })
      .then(d => {
        try { localStorage.setItem(sessionKey, JSON.stringify(d)); } catch { /* ignore */ }
        setSession(d);
        setState('chatting');
      })
      .catch(e => { setErrorMsg(e.message); setJoining(false); });
  };

  const patch = (id, fn) => setMessages(prev => prev.map(m => (String(m.id) === String(id) ? fn(m) : m)));

  useEffect(() => {
    if (state !== 'chatting' || !session) return;
    guestFetch(`/api/guest/groups/${session.groupId}/messages`, { token: session.guestToken })
      .then(d => setMessages((d.data || []).slice().reverse()))
      .catch(() => {});

    const socket = io(BASE, { auth: { token: session.guestToken }, transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('group:message:new', (msg) => {
      if (msg.group_id !== session.groupId) return;
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev;
        if (msg.clientId && prev.some(m => m.clientId === msg.clientId)) {
          return prev.map(m => m.clientId === msg.clientId ? msg : m);
        }
        return [...prev, msg];
      });
    });
    // Someone reacted: the pushed summary is from the reactor's point of view, so ask the server who reacted and rebuild mine.
    socket.on('message:reaction', ({ messageId }) => {
      guestFetch(`/api/guest/groups/${session.groupId}/messages/${messageId}/reactions`, { token: session.guestToken }).then(d => {
        const by = new Map();
        for (const r of d.data || []) { const e = by.get(r.emoji) || { emoji: r.emoji, count: 0, reacted_by_me: false }; e.count++; if (r.is_me) e.reacted_by_me = true; by.set(r.emoji, e); }
        setMessages(prev => prev.map(m => (String(m.id) === String(messageId) ? { ...m, reactions: [...by.values()] } : m)));
      }).catch(() => {});
    });
    socket.on('message:deleted', ({ messageId }) => setMessages(prev => prev.map(m => (String(m.id) === String(messageId)
      ? { ...m, deleted_at: new Date().toISOString(), body: '', attachment_kind: null, attachment_path: null, reactions: [] } : m))));
    socket.on('message:edited', ({ messageId, body, edited_at }) => setMessages(prev => prev.map(m => (String(m.id) === String(messageId) ? { ...m, body, edited_at } : m))));
    return () => socket.disconnect();
  }, [state, session]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  const send = () => {
    const body = text.trim();
    if (!body || !session) return;
    const clientId = 'g' + Date.now() + Math.random().toString(36).slice(2);
    setMessages(prev => [...prev, { id: clientId, clientId, sender_id: myId, body, created_at: new Date().toISOString(), _optimistic: true }]);
    setText('');
    socketRef.current?.emit('group:message:send', { groupId: session.groupId, body, clientId }, (res) => {
      if (res?.ok) setMessages(prev => prev.map(m => m.clientId === clientId ? res.message : m));
    });
  };

  const uploadFile = (file) => {
    if (!file || !session) return;
    const clientId = 'g' + Date.now() + Math.random().toString(36).slice(2);
    const previewUrl = URL.createObjectURL(file);
    const kind = attachmentKindFor(file);
    setMessages(prev => [...prev, {
      id: clientId, clientId, sender_id: myId, created_at: new Date().toISOString(),
      attachment_kind: kind, attachment_name: file.name, attachment_size: file.size, _previewUrl: previewUrl, _optimistic: true, _uploading: true,
    }]);
    const fd = new FormData();
    fd.append('file', file);
    guestFetch(`${gp}/messages/attachment`, { method: 'POST', token: tok, body: fd, isForm: true })
      .then(row => { URL.revokeObjectURL(previewUrl); setMessages(prev => prev.map(m => m.clientId === clientId ? row : m)); })
      .catch(() => setMessages(prev => prev.map(m => m.clientId === clientId ? { ...m, _uploading: false, _failed: true } : m)));
  };

  const onPickFile = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    files.forEach(uploadFile);
  };

  // ── voice notes ──
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recChunksRef.current = []; recCancelRef.current = false;
      rec.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        if (recCancelRef.current) return;
        const blob = new Blob(recChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size > 0) uploadFile(new File([blob], 'voice-message.webm', { type: blob.type }));
      };
      recorderRef.current = rec; rec.start();
      setRecording(true); setRecSecs(0);
      recTimerRef.current = setInterval(() => setRecSecs(x => x + 1), 1000);
    } catch (err) { console.error('Mic access denied or unavailable:', err); }
  };
  const stopRecording = (cancel = false) => {
    clearInterval(recTimerRef.current); setRecording(false);
    recCancelRef.current = cancel; if (cancel) recChunksRef.current = [];
    recorderRef.current?.stop();
  };

  // ── message actions ──
  const react = (id, emoji) => {
    setMenu(null);
    if (String(id).startsWith('g')) return;
    guestFetch(`${gp}/messages/${id}/react`, { method: 'POST', token: tok, body: { emoji } })
      .then(d => patch(id, m => ({ ...m, reactions: d.reactions })))
      .catch(() => {});
  };
  const openWho = (e, m, emoji) => {
    const r = e.currentTarget.getBoundingClientRect();
    setWho({ id: m.id, emoji, anchor: { left: r.left, top: r.top, bottom: r.bottom }, rows: null });
    guestFetch(`${gp}/messages/${m.id}/reactions`, { token: tok })
      .then(d => setWho(w => (w ? { ...w, rows: d.data || [] } : w)))
      .catch(() => setWho(w => (w ? { ...w, rows: [] } : w)));
  };
  const deleteMsg = (id) => {
    setMenu(null);
    if (!window.confirm('Delete this message for everyone?')) return;
    guestFetch(`${gp}/messages/${id}`, { method: 'DELETE', token: tok }).catch(() => {});
  };
  const saveEdit = () => {
    const body = editing.text.trim();
    guestFetch(`${gp}/messages/${editing.id}`, { method: 'PUT', token: tok, body: { body } })
      .then(() => { patch(editing.id, m => ({ ...m, body, edited_at: new Date().toISOString() })); setEditing(null); })
      .catch(e => setEditing(ed => ({ ...ed, err: e.message })));
  };

  if (state === 'loading') {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-slate-400" size={28} /></div>;
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-[380px] w-full bg-white border border-slate-200 rounded-2xl p-6 text-center shadow-sm">
          <div className="text-[15px] font-bold text-slate-800 mb-1">Can't open this invite</div>
          <div className="text-[13px] text-slate-500">{errorMsg}</div>
        </div>
      </div>
    );
  }

  if (state === 'join') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-[380px] w-full bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="text-[16px] font-extrabold text-slate-800 mb-1">You've been invited to "{invite.groupName}"</div>
          <div className="text-[13px] text-slate-500 mb-4">This link only opens this one conversation — no account or full access is created for you.</div>
          <label className="block text-[12px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Your name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="w-full mb-4 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 outline-none" />
          <button onClick={join} disabled={joining} className="w-full py-[10px] rounded-lg bg-emerald-600 text-white text-[13.5px] font-bold border-0 cursor-pointer disabled:opacity-60">
            {joining ? 'Joining…' : 'Join the conversation'}
          </button>
          {errorMsg && <div className="text-[12px] text-red-600 mt-2">{errorMsg}</div>}
        </div>
      </div>
    );
  }

  const menuMsg = menu ? messages.find(x => String(x.id) === String(menu.id)) : null;
  const whoRows = who?.rows ? [...who.rows].sort((a, b) => (b.emoji === who.emoji) - (a.emoji === who.emoji)) : null;
  const fmtSecs = (x) => `${Math.floor(x / 60)}:${String(x % 60).padStart(2, '0')}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-6">
      <div className="w-full max-w-[480px] h-[84vh] bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="text-[14px] font-extrabold text-slate-800 truncate">{session.groupName}</div>
          <div className="text-[11px] text-slate-500">Guest conversation</div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-[8px] bg-slate-50">
          {messages.length === 0 && <div className="flex-1 flex items-center justify-center text-[13px] text-slate-400">No messages yet — say hello!</div>}
          {messages.map((m, idx) => {
            const mine = m.sender_id === myId || m._optimistic;
            if (m.system_event) return <div key={m.id || idx} className="text-center text-[11px] text-slate-400">{m.body}</div>;
            if (m.deleted_at) {
              return (
                <div key={m.id || idx} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className="rounded-2xl px-3 py-[7px] text-[12px] italic text-slate-400 bg-white border border-slate-200">🚫 This message was deleted <span className="text-[10px] not-italic ml-1">{fmtTime(m.created_at)}</span></div>
                </div>
              );
            }
            const hasAttachment = !!m.attachment_kind;
            const canAct = !m._optimistic && !String(m.id).startsWith('g');
            return (
              <div key={m.id || idx} className={`group flex items-end gap-[4px] ${mine ? 'justify-end' : 'justify-start'}`}>
                {mine && canAct && (
                  <button type="button" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu({ id: m.id, anchor: { left: r.right - 190, top: r.top, bottom: r.bottom }, picker: false }); }}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-[22px] h-[22px] rounded-full bg-white border border-slate-200 text-slate-400 flex items-center justify-center cursor-pointer" title="More"><MoreHorizontal size={13} /></button>
                )}
                <div className={`flex flex-col max-w-[78%] ${mine ? 'items-end' : 'items-start'}`}>
                  <div className={`rounded-2xl text-[13px] leading-[1.4] ${hasAttachment ? `bg-white border border-slate-200 text-slate-800 ${m.attachment_kind === 'image' ? 'p-[3px]' : 'p-2'}` : `px-3 py-[7px] ${mine ? 'bg-emerald-600 text-white rounded-br-[4px]' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-[4px]'}`}`}>
                    {!mine && <div className="text-[10.5px] font-bold text-emerald-700 mb-[2px] px-[2px]">{m.sender_name}</div>}
                    {hasAttachment && <div className={m._uploading ? 'opacity-60' : ''}><GuestAttachment m={m} onOpenImage={setLightbox} /></div>}
                    {m.body && <div className={`break-words whitespace-pre-wrap ${hasAttachment ? 'px-[6px] pt-[6px]' : ''}`}>{renderBody(m.body)}</div>}
                    <div className={`flex items-center justify-end gap-1 text-[9.5px] mt-[2px] ${hasAttachment ? 'text-slate-400 px-[6px] pb-[2px]' : (mine ? 'text-white/75' : 'text-slate-400')}`}>
                      {m._uploading && <span>Uploading…</span>}{m._failed && <span>Failed to send</span>}
                      {m.edited_at && <span>Edited</span>}
                      <span>{fmtTime(m.created_at)}</span>
                    </div>
                  </div>
                  {m.reactions?.length > 0 && (
                    <div className={`flex flex-wrap gap-[4px] mt-[3px] ${mine ? 'justify-end' : 'justify-start'}`}>
                      {m.reactions.map(r => (
                        <button key={r.emoji} type="button" onClick={(e) => openWho(e, m, r.emoji)} title="See who reacted"
                          className={`flex items-center gap-[3px] px-[7px] py-[1px] rounded-full border text-[11px] cursor-pointer ${r.reacted_by_me ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-slate-200 text-slate-600'}`}>
                          <span>{r.emoji}</span>{r.count > 1 && <span className="font-semibold">{r.count}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {!mine && canAct && (
                  <button type="button" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu({ id: m.id, anchor: { left: r.left, top: r.top, bottom: r.bottom }, picker: false }); }}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity w-[22px] h-[22px] rounded-full bg-white border border-slate-200 text-slate-400 flex items-center justify-center cursor-pointer" title="More"><MoreHorizontal size={13} /></button>
                )}
              </div>
            );
          })}
        </div>

        {recording ? (
          <div className="flex items-center gap-3 px-3 py-[10px] border-t border-slate-200 bg-white">
            <span className="w-[9px] h-[9px] rounded-full bg-red-500 animate-pulse" />
            <span className="text-[13px] text-slate-700 flex-1">Recording… {fmtSecs(recSecs)}</span>
            <button onClick={() => stopRecording(true)} className="w-[34px] h-[34px] rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center border-0 cursor-pointer" title="Cancel"><X size={16} /></button>
            <button onClick={() => stopRecording(false)} className="w-[34px] h-[34px] rounded-lg bg-emerald-600 text-white flex items-center justify-center border-0 cursor-pointer" title="Send voice message"><Send size={15} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-[10px] border-t border-slate-200 bg-white">
            <input ref={fileInputRef} type="file" multiple onChange={onPickFile} className="hidden" />
            <button onClick={() => fileInputRef.current?.click()} className="w-[34px] h-[34px] rounded-lg bg-transparent text-slate-500 flex items-center justify-center border-0 cursor-pointer hover:bg-slate-100" title="Attach photos, videos, audio or files"><Paperclip size={16} /></button>
            <button onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setComposerEmoji({ left: r.left, top: r.top, bottom: r.bottom }); }} className="w-[34px] h-[34px] rounded-lg bg-transparent text-slate-500 flex items-center justify-center border-0 cursor-pointer hover:bg-slate-100" title="Emoji"><Smile size={17} /></button>
            <input
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message..."
              className="flex-1 py-[8px] px-[12px] bg-slate-100 border border-slate-200 rounded-lg text-slate-800 text-[13px] outline-none"
            />
            {text.trim() ? (
              <button onClick={send} className="w-[34px] h-[34px] rounded-lg bg-emerald-600 text-white flex items-center justify-center border-0 cursor-pointer"><Send size={15} /></button>
            ) : (
              <button onClick={startRecording} className="w-[34px] h-[34px] rounded-lg bg-emerald-600 text-white flex items-center justify-center border-0 cursor-pointer" title="Record a voice message"><Mic size={15} /></button>
            )}
          </div>
        )}
      </div>

      {composerEmoji && (
        <Popover anchor={composerEmoji} width={268} height={250} onClose={() => setComposerEmoji(null)}>
          <EmojiGrid onPick={(emoji) => setText(t => t + emoji)} />
        </Popover>
      )}

      {menu && menuMsg && (
        <Popover anchor={menu.anchor} width={menu.picker ? 268 : 190} height={menu.picker ? 260 : 190} onClose={() => setMenu(null)}>
          {menu.picker ? <EmojiGrid onPick={(emoji) => react(menuMsg.id, emoji)} /> : (
            <>
              <div className="flex items-center justify-between px-[8px] py-[6px] border-b border-slate-100">
                {QUICK_REACTIONS.map(emoji => (
                  <button key={emoji} type="button" onClick={() => react(menuMsg.id, emoji)} className="text-[16px] bg-transparent border-0 cursor-pointer p-[2px] rounded-md hover:bg-slate-100 leading-none">{emoji}</button>
                ))}
                <button type="button" onClick={() => setMenu(mn => ({ ...mn, picker: true }))} className="w-[20px] h-[20px] rounded-md bg-slate-100 text-slate-500 flex items-center justify-center border-0 cursor-pointer" title="More emoji"><Plus size={12} /></button>
              </div>
              {menuMsg.body && (
                <button type="button" onClick={() => { navigator.clipboard?.writeText(menuMsg.body).catch(() => {}); setMenu(null); }} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-slate-700 bg-transparent border-0 cursor-pointer hover:bg-slate-50"><Copy size={13} className="text-slate-400" /> Copy</button>
              )}
              {menuMsg.sender_id === myId && !menuMsg.attachment_kind && menuMsg.body && Date.now() - new Date(menuMsg.created_at).getTime() < EDIT_WINDOW_MS && (
                <button type="button" onClick={() => { setEditing({ id: menuMsg.id, text: menuMsg.body, err: '' }); setMenu(null); }} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-slate-700 bg-transparent border-0 cursor-pointer hover:bg-slate-50"><Pencil size={13} className="text-slate-400" /> Edit</button>
              )}
              {menuMsg.sender_id === myId && (
                <button type="button" onClick={() => deleteMsg(menuMsg.id)} className="w-full flex items-center gap-[6px] text-left px-3 py-[7px] text-[12px] font-semibold text-red-600 bg-transparent border-0 cursor-pointer hover:bg-slate-50"><Trash2 size={13} /> Delete</button>
              )}
            </>
          )}
        </Popover>
      )}

      {who && (
        <Popover anchor={who.anchor} width={220} height={240} onClose={() => setWho(null)}>
          <div className="px-3 py-[5px] text-[11px] font-bold text-slate-400 border-b border-slate-100">Reactions</div>
          <div className="max-h-[200px] overflow-y-auto">
            {!whoRows && <div className="px-3 py-2 text-[12px] text-slate-400">Loading…</div>}
            {whoRows && whoRows.length === 0 && <div className="px-3 py-2 text-[12px] text-slate-400">No reactions</div>}
            {whoRows && whoRows.map((x, i) => (
              <div key={x.user_id + x.emoji + i} className="flex items-center gap-2 px-3 py-[6px] text-[12px] text-slate-800">
                <span className="text-[15px] leading-none">{x.emoji}</span>
                <span className="flex-1 truncate font-semibold">{x.is_me ? 'You' : x.name}</span>
                {x.is_me && <button onClick={() => { const id = who.id; setWho(null); react(id, x.emoji); }} className="text-[11px] font-semibold text-red-600 bg-transparent border-0 cursor-pointer hover:underline">Remove</button>}
              </div>
            ))}
          </div>
        </Popover>
      )}

      {editing && (
        <div className="fixed inset-0 z-[700] bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-[420px] bg-white rounded-xl shadow-xl p-4" onClick={e => e.stopPropagation()}>
            <div className="text-[13px] font-bold text-slate-800 mb-2">Edit message</div>
            <textarea autoFocus value={editing.text} onChange={e => setEditing(ed => ({ ...ed, text: e.target.value }))} rows={4} className="w-full text-[13px] text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none resize-none" />
            {editing.err && <div className="text-[12px] text-red-600 mt-1">{editing.err}</div>}
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setEditing(null)} className="px-3 py-[6px] text-[12px] font-semibold rounded-lg bg-slate-100 text-slate-700 border-0 cursor-pointer">Cancel</button>
              <button disabled={!editing.text.trim()} onClick={saveEdit} className="px-3 py-[6px] text-[12px] font-semibold rounded-lg bg-emerald-600 text-white border-0 cursor-pointer disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[700] bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button type="button" aria-label="Close" onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-[34px] h-[34px] rounded-full bg-white/15 text-white border-0 cursor-pointer flex items-center justify-center"><X size={18} /></button>
          <img src={lightbox} alt="" className="max-w-[96vw] max-h-[92vh] rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
